import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CATEGORIES } from "@/lib/categories";
import { ArticleCard, ArticleRowThumb } from "@/components/ArticleCard";
import { FeaturedCarousel } from "@/components/FeaturedCarousel";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { ArrowRight, Flame, Newspaper } from "lucide-react";
import { Seo, organizationLd, websiteLd } from "@/components/Seo";

function SectionLabel({ children, icon: Icon, color = "#D97706" }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-meta text-[9px] leading-none uppercase tracking-[0.2em] text-white tape sticker-soft"
        style={{ background: color }}>
        {Icon && <Icon size={11} />} {children}
      </span>
      <span className="flex-1 h-0.5 border-t-2 border-dashed border-line" />
    </div>
  );
}

const HOME_SEO = (
  <Seo
    fullTitle="Collezionismo: notizie, valori e guide per collezionisti | Collezionatico"
    description="Collezionismo in un unico posto: notizie ogni giorno, valori indicativi e guide pratiche su carte Pokémon, orologi, auto d'epoca, monete, francobolli, fumetti, sneakers, LEGO, arte e memorabilia sportiva."
    path="/"
    jsonLd={[organizationLd, websiteLd]}
  />
);

function HomeIntro() {
  return (
    <section className="mt-8" data-testid="home-intro">
      <span className="tape font-meta text-[10px] uppercase tracking-[0.22em] text-gold">
        Il portale dei collezionisti
      </span>
      <h1 className="mt-4 font-editorial font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-stone-900">
        <span className="mark-hl">Collezionismo</span>: notizie, valori<br className="hidden sm:block" /> e guide per chi colleziona
      </h1>
      <p className="mt-5 max-w-3xl text-stone-600 leading-relaxed">
        Ogni giorno raccogliamo e riscriviamo in italiano ciò che si muove nel mercato del collezionismo, con
        fonte e link all'originale: <Link to="/categoria/carte-pokemon" className="underline decoration-gold decoration-2 underline-offset-2 hover:text-stone-900">carte Pokémon e TCG</Link>,{" "}
        <Link to="/categoria/orologi" className="underline decoration-gold decoration-2 underline-offset-2 hover:text-stone-900">orologi da collezione</Link>,{" "}
        <Link to="/categoria/auto" className="underline decoration-gold decoration-2 underline-offset-2 hover:text-stone-900">auto d'epoca</Link>,{" "}
        <Link to="/categoria/monete" className="underline decoration-gold decoration-2 underline-offset-2 hover:text-stone-900">monete</Link>,{" "}
        <Link to="/categoria/francobolli" className="underline decoration-gold decoration-2 underline-offset-2 hover:text-stone-900">francobolli</Link>,{" "}
        <Link to="/categoria/fumetti" className="underline decoration-gold decoration-2 underline-offset-2 hover:text-stone-900">fumetti</Link>,{" "}
        <Link to="/categoria/sneakers" className="underline decoration-gold decoration-2 underline-offset-2 hover:text-stone-900">sneakers</Link>,{" "}
        <Link to="/categoria/lego" className="underline decoration-gold decoration-2 underline-offset-2 hover:text-stone-900">LEGO</Link>,{" "}
        <Link to="/categoria/arte" className="underline decoration-gold decoration-2 underline-offset-2 hover:text-stone-900">arte e antiquariato</Link> e{" "}
        <Link to="/categoria/memorabilia-sportivi" className="underline decoration-gold decoration-2 underline-offset-2 hover:text-stone-900">memorabilia sportiva</Link>.
        Trovi anche le <Link to="/valori" className="underline decoration-gold decoration-2 underline-offset-2 hover:text-stone-900">Schede Valore</Link> con
        fasce di prezzo indicative e le <Link to="/guide" className="underline decoration-gold decoration-2 underline-offset-2 hover:text-stone-900">guide per iniziare</Link>:
        cosa comprare, quando e perché. Se parti da zero, leggi la{" "}
        <Link to="/collezionismo" data-testid="home-link-pillar" className="underline decoration-gold decoration-2 underline-offset-2 hover:text-stone-900">guida completa al collezionismo</Link>.
        Ogni articolo è validato a mano dalla redazione prima della pubblicazione.
      </p>
    </section>
  );
}

export default function HomePage() {
  const [articles, setArticles] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setFailed(false);
    Promise.allSettled([
      api.get("/articles", { params: { limit: 60 } }),
      api.get("/featured"),
    ])
      .then(([all, feat]) => {
        if (all.status === "fulfilled") setArticles(all.value.data);
        if (feat.status === "fulfilled") setFeatured(feat.value.data);
        if (all.status === "rejected") setFailed(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Carosello: le notizie in evidenza (max 10, la più recente per prima); se non ce n'è, la più recente.
  const carousel = featured.length ? featured : articles.slice(0, 1);
  const carouselIds = new Set(carousel.map((a) => a.id));
  const notFeatured = articles.filter((a) => !carouselIds.has(a.id));
  // "Notizie di oggi": pubblicate oggi e non in evidenza; se sono poche, si completa con le più recenti.
  const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
  const publishedToday = notFeatured.filter((a) => new Date(a.published_at) >= startOfDay);
  const today = publishedToday.length >= 4
    ? publishedToday.slice(0, 7)
    : [...publishedToday, ...notFeatured.filter((a) => !publishedToday.includes(a))].slice(0, 6);
  const todayIds = new Set(today.map((a) => a.id));
  const latest = notFeatured.filter((a) => !todayIds.has(a.id)).slice(0, 6);
  const hero = carousel[0];

  if (loading) return <>{HOME_SEO}<LoadingHome /></>;
  if (failed) return <>{HOME_SEO}<ConnectionError onRetry={load} /></>;
  if (!hero) return <>{HOME_SEO}<EmptyHome /></>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {HOME_SEO}
      <HomeIntro />
      <div className="mt-10"><DisclaimerBanner compact /></div>

      <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 mt-10 fade-up">
        <div className="lg:col-span-7">
          <SectionLabel icon={Flame} color="#E11D48">In Evidenza</SectionLabel>
          <FeaturedCarousel articles={carousel} />
        </div>

        <div className="lg:col-span-5">
          <SectionLabel icon={Newspaper} color="#7C3AED">Notizie di Oggi</SectionLabel>
          <div data-testid="today-news-list">
            {today.map((a) => <ArticleRowThumb key={a.id} article={a} />)}
            {today.length === 0 && (
              <p className="text-sm text-stone-500">Nessuna altra notizia oggi: torna più tardi.</p>
            )}
          </div>
        </div>
      </section>

      <section className="mt-20">
        <div className="flex items-end justify-between gap-4 mb-8">
          <h2 className="font-editorial font-extrabold text-2xl sm:text-3xl tracking-tight text-stone-900">
            Dalla <span className="mark-hl">Redazione</span>
          </h2>
          <span className="hidden sm:block flex-1 h-0.5 border-t-2 border-dashed border-line mb-3" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {latest.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}
        </div>
      </section>

      {CATEGORIES.map((cat) => {
        const items = articles.filter((a) => a.category === cat.slug).slice(0, 3);
        if (items.length === 0) return null;
        return (
          <section key={cat.slug} className="mt-20">
            <div className="flex items-center justify-between gap-4 mb-6">
              <h2 className="font-editorial font-bold text-xl sm:text-2xl tracking-tight inline-flex items-center gap-2.5 rounded-full px-4 py-1.5 tape text-white sticker-soft"
                style={{ background: cat.hex }}>
                {cat.name}
              </h2>
              <Link to={`/categoria/${cat.slug}`} data-testid={`home-cat-more-${cat.slug}`}
                className="nav-pill shrink-0">
                Tutti <ArrowRight size={13} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function LoadingHome() {
  return (
    <div className="max-w-7xl mx-auto px-4 py-16">
      <div className="animate-pulse grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 h-[420px] rounded-2xl bg-panel" />
        <div className="lg:col-span-5 space-y-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-20 rounded-xl bg-panel" />)}
        </div>
      </div>
    </div>
  );
}

function ConnectionError({ onRetry }) {
  return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="home-connection-error">
      <h2 className="font-editorial font-extrabold text-3xl text-stone-900">Non riusciamo a caricare le notizie</h2>
      <p className="text-stone-600 mt-4">
        Gli articoli ci sono, ma il collegamento con il server non ha risposto in tempo. Riprova tra un istante.
      </p>
      <button onClick={onRetry} data-testid="home-retry-btn"
        className="mt-6 inline-flex items-center gap-2 bg-stone-900 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-stone-800 transition-colors">
        Riprova
      </button>
    </div>
  );
}

function EmptyHome() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center">
      <h2 className="font-editorial font-extrabold text-3xl text-stone-900">Nessun articolo pubblicato</h2>
      <p className="text-stone-600 mt-4">La redazione sta preparando i primi contenuti. Torna presto.</p>
    </div>
  );
}
